import { constant, noop } from 'lodash';
import { type ReactNode, useCallback, useMemo } from 'react';

import { type PublicClip } from '@air/api';
import { type Clip } from '@air/api/types';
import { type GallerySection } from '@air/component-gallery-view';
import { useBreakpointsContext } from '@air/provider-media-query';
import { type Upload } from '@air/redux-uploader';

import { type GalleryMetadata } from '~/components/Gallery/galleryMetadata';
import { GalleryItemType, type GalleryViewRender } from '~/components/Gallery/types';
import { UploadingFileGalleryCard } from '~/components/Gallery/UploadingFileGalleryCard';
import { useGalleryDynamicSectionFooters } from '~/components/Gallery/useGalleryDynamicSectionFooters';
import { useGalleryDynamicSectionHeaders } from '~/components/Gallery/useGalleryDynamicSectionHeaders';
import { DragType } from '~/components/Shared/Drag/dragTypes';
import { WKSPC_DESKTOP_HORIZONTAL_PADDING_WITH_SIDENAV_OPEN } from '~/constants/WorkspaceSpacing';
import useCSSGridLikeLayoutsForMultiSections from '~/hooks/useCSSGridLikeLayoutsForMultiSections';
import { type UseGalleryDataInfo } from '~/swr-hooks/gallery/types';

type GalleryDynamicSection<C> = {
  groupName: string;
  data?: UseGalleryDataInfo<C>;
  uploads?: Record<string, Upload>;
  renderTitle?: ({ total }: { total: number }) => ReactNode;
  loadMore?: () => void;
};

const DEFAULT_SORT_COMPARATOR = <C extends Clip | PublicClip>(
  a: GalleryDynamicSection<C>,
  b: GalleryDynamicSection<C>,
) => {
  return (a.data?.total || 0) > (b.data?.total || 0) ? 1 : -1;
};

export type UseGalleryDynamicSectionsParams<C> = {
  sections: GalleryDynamicSection<C>[];
  containerWidth: number;
  renderFile: ((props: GalleryViewRender<C>) => JSX.Element) | undefined;
  containerHorizontalPadding?: number;
  itemHeight: number;
  itemHorizontalSpacing?: number;
  itemWidth?: number;
  isSelectable?: (file: C) => boolean;
  sortComparator?: (a: GalleryDynamicSection<C>, b: GalleryDynamicSection<C>) => number;
};

export const useGalleryDynamicSections = <C extends Clip | PublicClip>({
  renderFile,
  sections,
  containerWidth,
  containerHorizontalPadding = WKSPC_DESKTOP_HORIZONTAL_PADDING_WITH_SIDENAV_OPEN,
  itemHeight,
  itemHorizontalSpacing = 24,
  itemWidth,
  isSelectable = constant(false),
  sortComparator = DEFAULT_SORT_COMPARATOR,
}: UseGalleryDynamicSectionsParams<C>): (GallerySection<GalleryMetadata> | null)[] => {
  const { isAboveSmallScreen } = useBreakpointsContext();

  const sortedSections = useMemo(() => {
    return sections.sort((a, b) => {
      return sortComparator(a, b);
    });
  }, [sections, sortComparator]);

  const sectionFiles = useMemo(
    () =>
      sortedSections.map((s) => [
        ...(s.uploads ? Object.values(s.uploads) : []).map((u) => {
          return {
            item: u,
            type: GalleryItemType.upload,
            isUploading: true,
          } as const;
        }),
        ...(s.data?.items ?? []).map((u) => {
          return {
            item: u,
            type: GalleryItemType.file,
            isUploading: false,
          } as const;
        }),
      ]),
    [sortedSections],
  );

  const { sections: sectionFileBoxes } = useCSSGridLikeLayoutsForMultiSections({
    sections: sectionFiles.map((s) => ({
      height: itemHeight,
      minWidth: itemWidth ?? itemHeight * 1.25,
      itemCount: s.length,
      containerWidth,
      containerHorizontalPadding,
      itemHorizontalSpacing,
    })),
  });

  const renderItem = useCallback(
    (sectionIndex: number, index: number) => {
      const file = sectionFiles[sectionIndex][index];

      if (file) {
        return file.type === GalleryItemType.upload ? (
          <UploadingFileGalleryCard
            width={
              /**
               * Uploads don't inherently have a width and get it from the ratio of the
               * row height
               */
              itemHeight
            }
            upload={file.item}
          />
        ) : (
          renderFile?.({
            box: sectionFileBoxes[sectionIndex][index],
            data: file.item,
            index,
          })
        );
      }

      return null;
    },
    [sectionFiles, itemHeight, renderFile, sectionFileBoxes],
  );

  const getItemData = useCallback(
    (sectionIndex: number, index: number): GalleryMetadata => ({
      type: sectionFiles[sectionIndex][index].type,
      isSelectable:
        sectionFiles[sectionIndex][index].type === GalleryItemType.file
          ? isSelectable(sectionFiles[sectionIndex][index].item)
          : false,
      itemId: sectionFiles[sectionIndex][index].item.id,
      canDragTo: (item) => item.type === DragType.file,
    }),
    [sectionFiles, isSelectable],
  );

  const sectionHeaders = useGalleryDynamicSectionHeaders(
    sortedSections.map((s, index) => {
      const total = (s.data?.total || 0) + (Object.values(s.uploads ?? {}).length || 0);

      return {
        groupName: s.groupName,
        isFirstSection: index === 0,
        title: s.renderTitle ? s.renderTitle({ total }) : `Files (${total.toLocaleString()})`,
        containerWidth,
        containerPadding: containerHorizontalPadding,
      };
    }),
  );

  const sectionFooters = useGalleryDynamicSectionFooters(
    sortedSections.map((s) => ({
      groupName: s.groupName,
      isLoading: !!s.data?.isLoading,
      loadMore: s.loadMore ?? noop,
      hasMore: !!s.data?.hasMore,
      containerWidth,
      containerPadding: containerHorizontalPadding,
    })),
  );

  return sortedSections.map((s, index) => {
    const { header, isHeaderCollapsed } = sectionHeaders[index];
    const footer = sectionFooters[index];
    const total = (s.data?.total || 0) + (Object.values(s.uploads ?? {}).length || 0);
    const fileBoxes = sectionFileBoxes[index];

    if (!total && !s.data?.isLoading) {
      return null;
    }

    return {
      header: total ? header : undefined,
      footer: footer
        ? {
            box: footer.box,
            render: (index: number, isVisible: boolean) => footer.render(index, !isHeaderCollapsed && isVisible),
          }
        : undefined,
      items: {
        render: (i: number) => renderItem(index, i),
        boxes: isHeaderCollapsed ? [] : fileBoxes,
        verticalItemsSpacing: !isAboveSmallScreen ? 12 : 24,
        getData: (i: number) => getItemData(index, i),
      },
    };
  });
};
